/** @format */

//TODO: remove export from unused functions and interfaces

import axios, { AxiosProgressEvent } from "axios";
import { UserToken } from "../../auth/authSlice";

interface S3UrlResponseFields {
  acl: "private";
  "Content-Type": "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
  key: string; //"uploads/Storyboard%20-%20EBL22-23_1.5%20Socio%20cultural.docx",
  "x-amz-algorithm": "AWS4-HMAC-SHA256";
  "x-amz-credential": string; //"XXXXXXXXXXXXXXXX/20230731/eu-west-2/s3/aws4_request",
  "x-amz-date": string; // "20230731T110648Z",
  "x-amz-security-token": string;
  policy: string;
  "x-amz-signature": string;
  [key: string]: string | Blob;
}
export interface S3UrlResponse {
  url: string;
  fields: S3UrlResponseFields;
}

export interface getJsonReturn {
  messages: {}[];
  resource: {};
  user: {};
}

export class BLUploadService {
  private axiosClient;

  constructor(oauthToken: string) {
    this.axiosClient = axios.create({
      baseURL:
        process.env.REACT_APP_SB_IMPORT_API_URL ||
        "https://m9fk55l7t0.execute-api.eu-west-2.amazonaws.com/dev/", // storyboard-import stack
      // baseURL: 'https://mbwi1e1kfd.execute-api.localhost.localstack.cloud:4566/dev/',
      headers: {
        Authorization: `Bearer ${oauthToken}`,
      },
    });
  }

  async getSignedS3Url(fileName: string) {
    const response = await this.axiosClient.get(`uploads/${fileName}`);
    const { data, status } = response;
    if (status >= 300) throw Error();
    return data as S3UrlResponse;
  }

  async postToS3(
    url: string,
    fields: FormData,
    onUploadProgress?: (progressEvent: AxiosProgressEvent) => void,
  ) {
    const response = await axios.post(url, fields, {
      onUploadProgress,
    });
    return response;
  }

  async getJson(fileName: string) {
    const { data } = await this.axiosClient.get(`import/${fileName}`);
    return data;
  }
}

/**
 * converts a storyboard file to a json object
 *
 * @param userToken the user tokern required to acess the BLUploadService
 * @param uploadedFile the storyboard to be converted to a json object
 * @returns a promise containing the converted json object as resolve, or an error message (string) as reject
 */
export const storyboardToJSON = async (
  userToken: UserToken,
  uploadedFile: File,
): Promise<Object> => {
  return new Promise(async (resolve, reject) => {
    const client = new BLUploadService(userToken.token);

    // Get signed S3URL
    let response;
    try {
      response = await client.getSignedS3Url(uploadedFile.name);
    } catch (error) {
      // Failed to authorise
      reject(`Failed to get Signed URL: ${(error as Error).message}`);
      return;
    }

    // Create form data
    const formData = new FormData();
    const fieldKeys = Object.keys(response.fields);
    for (let i = 0; i < fieldKeys.length; i++) {
      formData.append(fieldKeys[i], response.fields[fieldKeys[i]]);
    }
    formData.append("file", uploadedFile);

    // Post form data
    let S3Response;
    try {
      S3Response = await client.postToS3(response.url, formData);
    } catch (error) {
      // error during post
      reject(`Failed upload file: ${(error as Error).message}`);
      return;
    }

    // error in post response
    if (!S3Response || S3Response.status > 299) {
      reject("Failed upload file");
      return;
    }

    let data: getJsonReturn;
    try {
      data = await client.getJson(uploadedFile.name);
    } catch (error) {
      // Failed to get JSON
      reject(`Failed converting to JSON: ${(error as Error).message}`);
      return;
    }

    resolve(data);
  });
};

/**
 * Format bytes as human-readable text.
 *
 * @param bytes Number of bytes.
 * @param si True to use metric (SI) units, aka powers of 1000. False to use
 *           binary (IEC), aka powers of 1024.
 * @param dp Number of decimal places to display.
 *
 * @return Formatted string.
 */

export const humanFileSize = (bytes: number, si = false, dp = 1) => {
  const thresh = si ? 1000 : 1024;

  if (Math.abs(bytes) < thresh) {
    return bytes + " B";
  }

  const units = si
    ? ["kB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"]
    : ["KiB", "MiB", "GiB", "TiB", "PiB", "EiB", "ZiB", "YiB"];
  let u = -1;
  const r = 10 ** dp;

  do {
    bytes /= thresh;
    ++u;
  } while (
    Math.round(Math.abs(bytes) * r) / r >= thresh &&
    u < units.length - 1
  );

  return bytes.toFixed(dp) + " " + units[u];
};
